import { type ReactNode, useState } from 'react';
import {
  Platform,
  Pressable,
  type StyleProp,
  View,
  type ViewStyle,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Text from '@/components/UI/Text';

type HoverTooltipPlacement = 'top' | 'bottom';

type HoverTooltipProps = {
  children: ReactNode;
  disabled?: boolean;
  label?: string | null;
  placement?: HoverTooltipPlacement;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

type TooltipStyleOptions = {
  placement: HoverTooltipPlacement;
};

export default function HoverTooltip({
  children,
  disabled = false,
  label,
  placement = 'top',
  style: extraStyles,
  testID,
}: HoverTooltipProps) {
  const [hovered, setHovered] = useState(false);
  const showTooltip =
    Platform.OS === 'web' && hovered && !disabled && Boolean(label);

  return (
    <Pressable
      focusable={false}
      onHoverIn={() => {
        if (Platform.OS === 'web') {
          setHovered(true);
        }
      }}
      onHoverOut={() => {
        if (Platform.OS === 'web') {
          setHovered(false);
        }
      }}
      style={[style.host, extraStyles]}
      testID={testID}
    >
      {children}
      {showTooltip ? (
        <View pointerEvents="none" style={style.tooltip({ placement })}>
          <Text color="white" size="sm">
            {label}
          </Text>
        </View>
      ) : null}
    </Pressable>
  );
}

const style = StyleSheet.create((theme) => ({
  host: {
    position: 'relative',
  },
  tooltip: ({ placement }: TooltipStyleOptions) => ({
    alignSelf: 'center',
    backgroundColor: theme.colors.surfaceInverted,
    borderRadius: theme.borderRadius.md,
    bottom: placement === 'top' ? '100%' : undefined,
    boxShadow: '0 8px 18px rgba(0, 0, 0, 0.2)',
    maxWidth: theme.size(56),
    paddingHorizontal: theme.space(2),
    paddingVertical: theme.space(1),
    position: 'absolute',
    top: placement === 'bottom' ? '100%' : undefined,
    zIndex: 50,
  }),
}));
